<template>
  <ManagerLayout>
    <div class="manager-instructors">
      <h2 class="page-title">講師管理</h2>
      <p class="page-desc">登録講師の一覧・返信音声の再生・月別給与の確認ができます。</p>

      <!-- 講師別月別給与 CSV エクスポート -->
      <section class="manager-section">
        <h3 class="section-title">講師別月別給与 CSV</h3>
        <div class="card export-card">
          <div class="filters">
            <label class="filter-item">
              <span class="filter-label">年（任意）</span>
              <input v-model.number="exportYear" type="number" min="2020" max="2030" placeholder="例: 2025" class="form-input" />
            </label>
            <label class="filter-item">
              <span class="filter-label">月（任意）</span>
              <input v-model.number="exportMonth" type="number" min="1" max="12" placeholder="1-12" class="form-input" />
            </label>
            <button type="button" class="btn btn-primary" :disabled="exportCsvLoading" @click="downloadInstructorPayCsv">
              {{ exportCsvLoading ? '取得中...' : 'CSVダウンロード' }}
            </button>
          </div>
          <p class="export-note">年月を指定しない場合は直近24ヶ月分を出力します。</p>
        </div>
      </section>

      <!-- 講師一覧 -->
      <section class="manager-section">
        <h3 class="section-title">講師一覧</h3>
        <div class="card">
          <div class="toolbar">
            <span class="rate-note">単価: {{ monthlyRate }}円/件</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="listLoading" @click="loadInstructors">
              {{ listLoading ? '読み込み中...' : '更新' }}
            </button>
          </div>
          <div v-if="listLoading" class="loading-state">読み込み中...</div>
          <div v-else-if="!instructors.length" class="empty-state">講師が登録されていません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="講師一覧">
              <thead>
                <tr>
                  <th>表示名</th>
                  <th>名前</th>
                  <th>メール</th>
                  <th class="num">総FB数</th>
                  <th class="num">今月FB数</th>
                  <th class="num">今月給与</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in instructors" :key="u.id" class="data-row">
                  <td>{{ u.instructorDisplayName || '—' }}</td>
                  <td>{{ u.fullName || '—' }}</td>
                  <td>{{ u.email }}</td>
                  <td class="num">{{ u.totalFeedbackCount }}件</td>
                  <td class="num">{{ u.thisMonthCount }}件</td>
                  <td class="num">{{ formatYen(u.thisMonthSalary) }}</td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm"
                      :class="{ active: selectedId === u.id }"
                      @click="selectInstructor(u.id)"
                    >
                      {{ selectedId === u.id ? '閉じる' : '詳細' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- 講師詳細（選択時） -->
      <section v-if="selectedId" class="manager-section detail-section">
        <h3 class="section-title">講師詳細</h3>
        <div v-if="detailLoading" class="loading-state">読み込み中...</div>
        <template v-else-if="detail">
          <div class="card profile-card">
            <h4>{{ detail.profile.instructorDisplayName || detail.profile.fullName || '講師' }}</h4>
            <p class="profile-email">{{ detail.profile.email }}</p>
          </div>

          <div class="card">
            <h4>月別給与</h4>
            <p class="rate-note">単価: {{ detail.monthlyRate }}円/件</p>
            <div class="table-wrap">
              <table class="data-table" aria-label="月別給与">
                <thead>
                  <tr>
                    <th>年月</th>
                    <th class="num">件数</th>
                    <th class="num">給与</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in detail.monthlySummary" :key="m.yearMonth" class="data-row">
                    <td>{{ formatYearMonth(m.yearMonth) }}</td>
                    <td class="num">{{ m.count }}件</td>
                    <td class="num">{{ formatYen(m.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="card">
            <h4>返信音声一覧（直近500件）</h4>
            <div v-if="!detail.feedbacks.length" class="empty-state">該当なし</div>
            <div v-else class="table-wrap feedback-list-wrap">
              <table class="data-table" aria-label="返信音声一覧">
                <thead>
                  <tr>
                    <th>送信日時（JST）</th>
                    <th class="num">Day</th>
                    <th>種別</th>
                    <th>対象ユーザー（学習者・保護者）</th>
                    <th>講師返信</th>
                    <th>提出音声</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="fb in detail.feedbacks" :key="fb.id" class="data-row">
                    <td>{{ formatDateTimeJst(fb.feedbackSubmittedAt) }}</td>
                    <td class="num">{{ fb.day }}</td>
                    <td>{{ fb.type }}</td>
                    <td class="user-cell">
                      <span v-if="fb.learnerName || fb.parentEmail">
                        {{ fb.learnerName || '—' }}
                        <template v-if="fb.parentEmail">（{{ fb.parentEmail }}）</template>
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td>
                      <button
                        v-if="fb.hasAudio"
                        type="button"
                        class="btn btn-sm btn-play"
                        :disabled="playingId === fb.id"
                        @click="playFeedback(fb.id)"
                      >
                        {{ playingId === fb.id ? '再生中...' : '▶ 返信' }}
                      </button>
                      <span v-else class="no-audio">—</span>
                    </td>
                    <td>
                      <button
                        v-if="fb.hasSubmissionAudio"
                        type="button"
                        class="btn btn-sm btn-play"
                        :disabled="playingSubmissionId === fb.id"
                        @click="playSubmission(fb.id)"
                      >
                        {{ playingSubmissionId === fb.id ? '再生中...' : '▶ 提出' }}
                      </button>
                      <span v-else class="no-audio">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <audio
              ref="audioEl"
              controls
              class="feedback-audio-player"
              @ended="playingId = null; playingSubmissionId = null"
              @pause="playingId = null; playingSubmissionId = null"
              @error="playingId = null; playingSubmissionId = null"
            />
            <video
              v-show="playingMediaKind === 'video'"
              ref="feedbackVideoEl"
              controls
              playsinline
              class="feedback-video-review-player"
              @ended="playingId = null; playingSubmissionId = null"
              @pause="playingId = null; playingSubmissionId = null"
              @error="playingId = null; playingSubmissionId = null"
            />
          </div>
        </template>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const listLoading = ref(false)
const instructors = ref([])
const monthlyRate = ref(150)
const selectedId = ref(null)
const detail = ref(null)
const detailLoading = ref(false)
const playingId = ref(null)
const playingSubmissionId = ref(null)
const audioEl = ref(null)
const feedbackVideoEl = ref(null)
const playingMediaKind = ref('audio') // 「▶ 返信」再生中のメディア種別（audio/video）
const exportYear = ref('')
const exportMonth = ref('')
const exportCsvLoading = ref(false)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatYen (value) {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value ?? 0)
}

function formatYearMonth (ym) {
  if (!ym) return '—'
  const [y, m] = ym.split('-')
  return `${y}年${Number(m)}月`
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

async function downloadCsv (url, defaultFilename) {
  const token = await getToken()
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'ダウンロードに失敗しました')
  }
  const disposition = res.headers.get('Content-Disposition')
  const filename = disposition ? (disposition.match(/filename="([^"]+)"/)?.[1] || defaultFilename) : defaultFilename
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

const downloadInstructorPayCsv = async () => {
  exportCsvLoading.value = true
  try {
    const params = new URLSearchParams()
    if (exportYear.value) params.set('year', String(exportYear.value))
    if (exportMonth.value) params.set('month', String(exportMonth.value))
    const url = `${apiBase}/api/manager/export/instructor-pay?${params.toString()}`
    await downloadCsv(url, 'instructor-pay.csv')
  } catch (e) {
    alert(e.message || 'CSVの取得に失敗しました')
  } finally {
    exportCsvLoading.value = false
  }
}

const loadInstructors = async () => {
  listLoading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/instructors`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      instructors.value = []
      return
    }
    instructors.value = data.instructors || []
    monthlyRate.value = data.monthlyRate ?? 150
  } catch (_) {
    instructors.value = []
  } finally {
    listLoading.value = false
  }
}

const selectInstructor = async (id) => {
  if (selectedId.value === id) {
    selectedId.value = null
    detail.value = null
    return
  }
  selectedId.value = id
  detail.value = null
  detailLoading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/instructors/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      detail.value = null
      return
    }
    detail.value = data
  } catch (_) {
    detail.value = null
  } finally {
    detailLoading.value = false
  }
}

const playFeedback = async (submissionId) => {
  if (!selectedId.value || !audioEl.value) return
  playingSubmissionId.value = null
  playingId.value = submissionId
  try {
    const token = await getToken()
    const res = await fetch(
      `${apiBase}/api/manager/instructors/${selectedId.value}/feedback-audio?submissionId=${encodeURIComponent(submissionId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.signedUrl) {
      playingId.value = null
      alert(data.error || '音声の取得に失敗しました')
      return
    }
    if (data.mimeType && data.mimeType.startsWith('video/')) {
      audioEl.value.pause()
      audioEl.value.removeAttribute('src')
      playingMediaKind.value = 'video'
      if (feedbackVideoEl.value) {
        feedbackVideoEl.value.src = data.signedUrl
        feedbackVideoEl.value.play().catch(() => {
          playingId.value = null
        })
      }
    } else {
      if (feedbackVideoEl.value) {
        feedbackVideoEl.value.pause()
        feedbackVideoEl.value.removeAttribute('src')
      }
      playingMediaKind.value = 'audio'
      audioEl.value.src = data.signedUrl
      audioEl.value.play().catch(() => {
        playingId.value = null
      })
    }
  } catch (e) {
    playingId.value = null
    alert(e.message || '再生に失敗しました')
  }
}

const playSubmission = async (submissionId) => {
  if (!selectedId.value || !audioEl.value) return
  playingId.value = null
  playingSubmissionId.value = submissionId
  try {
    const token = await getToken()
    const res = await fetch(
      `${apiBase}/api/manager/instructors/${selectedId.value}/submission-audio?submissionId=${encodeURIComponent(submissionId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.signedUrl) {
      playingSubmissionId.value = null
      alert(data.error || '提出音声の取得に失敗しました')
      return
    }
    // 生徒の提出音声は常に音声のみ（動画提出は無いため分岐不要）
    if (feedbackVideoEl.value) {
      feedbackVideoEl.value.pause()
      feedbackVideoEl.value.removeAttribute('src')
    }
    playingMediaKind.value = 'audio'
    audioEl.value.src = data.signedUrl
    audioEl.value.play().catch(() => {
      playingSubmissionId.value = null
    })
  } catch (e) {
    playingSubmissionId.value = null
    alert(e.message || '再生に失敗しました')
  }
}

onMounted(() => {
  loadInstructors()
})
</script>

<style scoped>
.manager-instructors {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  color: #334155;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card h4 {
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  color: #475569;
}

.export-card .filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}
.export-card .filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.export-card .filter-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}
.export-card .form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
  width: 100px;
}
.export-note {
  margin: 0.75rem 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}
.btn-primary {
  background: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.rate-note {
  font-size: 0.875rem;
  color: #64748b;
}

.loading-state,
.empty-state {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.data-table th.num,
.data-table td.num {
  text-align: right;
}

.data-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.data-row:hover {
  background: #f8fafc;
}

.profile-card .profile-email {
  margin: 0.25rem 0 0 0;
  font-size: 0.9rem;
  color: #64748b;
}

.detail-section .card {
  margin-bottom: 1.5rem;
}

.btn-play {
  font-size: 0.8rem;
}

.btn.active {
  background: #334155;
  color: #f1f5f9;
}

.no-audio {
  color: #94a3b8;
}

.feedback-audio-player {
  margin-top: 1rem;
  width: 100%;
  max-width: 400px;
}

.feedback-video-review-player {
  margin-top: 1rem;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  display: block;
  background: #000;
  border-radius: 8px;
}

.feedback-list-wrap {
  max-height: 400px;
  overflow-y: auto;
}
</style>
